import { runInAction } from 'mobx'
import { task } from 'mobx-task'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { Job } from '@/features/jobs/Job'
import type { DraftJobStore } from '@/features/jobs/drafting/DraftJobStore'

/**
 * View model for confirming the deletion of a draft.
 */
export class DeleteDraftJobDialogViewModel {
  /**
   * Async prompt.
   */
  private readonly _prompt

  /**
   * The draft to delete.
   */
  private _job = Job.none

  /**
   * View model for the underlying modal.
   */
  readonly modal = new ModalViewModel()

  constructor(private readonly _draftJobStore: DraftJobStore) {
    this._prompt = new AsyncPrompt<boolean>({
      abort$: this.modal.close$,
    })
  }

  /**
   * Prompts the user to confirm deleting the draft.
   *
   * @returns Whether the draft was deleted.
   */
  async prompt({ job, signal }: PromptDeleteDraftJobOptions) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)

    runInAction(() => {
      this._job = job
      this.delete.reset()
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()
    return response ?? false
  }

  /**
   * Deletes the draft. A failure is shown in the dialog.
   */
  delete = task.resolved(async () => {
    await this._draftJobStore.archive(this._job.id)
    this._prompt.respond(true)
    this.modal.close()
  })
}

/**
 * Options for {@link DeleteDraftJobDialogViewModel.prompt}.
 */
export interface PromptDeleteDraftJobOptions {
  job: Job
  signal?: AbortSignal
}
